import React, { useEffect } from "react";
import { Input, Modal, Select } from "@tyrannosaurustech/ui";
import User from "../../../classes/User";
import { useForm, Controller } from "react-hook-form";

interface EditUserModalProps {
  isOpen: boolean;
  onSave: (user: User) => void;
  onClose: () => void;
  user: User;
  isLoading: boolean;
  canChangeRole: boolean;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  user,
  isLoading,
  canChangeRole,
}) => {
  const { control, formState, ...form } = useForm({
    mode: "onChange",
    defaultValues: {
      name: user.name,
      email: user.email,
      firmId: user.firmId,
      roles: user.roles,
      id: user.id,
    },
  });

  useEffect(() => {
    if (user) {
      form.reset({
        name: user.name,
        email: user.email,
        firmId: user.firmId,
        roles: user.roles,
        id: user.id,
      });
    }
  }, [user]);

  const rolesOptions = [
    { id: "clientmanager", label: "Admin" },
    { id: "clientmember", label: "User" },
  ];

  const onSubmit = form.handleSubmit((data) => {
    const formData = {
      ...data,
      roles: (data as any).roles?.value || data.roles,
    };

    onSave(formData as User);
  });

  return (
    <Modal
      swapButtons
      onConfirm={() => onSubmit()}
      open={isOpen}
      title="Edit User"
      confirmText="Save Changes"
      setOpen={onClose}
      loading={isLoading}
      disabled={!formState.isValid}
      buttonAlignment="right"
      titleClassName="text-left font-bold text-xl font-semibold not-italic capitalize"
      panelClassName="w-[600px] max-w-[1000px] max-h-[800px] min-h-[420px] "
    >
      <div className="flex flex-col gap-4 mt-4 mb-4">
        <Input
          label="Name"
          placeholder="Full Name"
          required
          className="w-full"
          {...form.register("name", { required: true })}
        />
        <Input
          label="Email"
          required
          className="w-full"
          {...form.register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
        />
        <Controller
          name="roles"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Select
              label="Role"
              value={rolesOptions.find((option) => option.id === value)}
              onChange={(selectedOption) => onChange(selectedOption?.id)}
              options={rolesOptions}
              className="w-full"
              disabled={!canChangeRole}
            />
          )}
        />
      </div>
    </Modal>
  );
};
